/** @jsxImportSource @emotion/react */
import PropTypes from 'prop-types'
import { useEffect, useRef } from 'react'
import { videoPlayerStyle } from '../styles'

/**
 * Extract YouTube video ID from URL
 * @param {string} url - YouTube URL
 * @returns {string} - Video ID
 */
const extractYouTubeId = (url) => {
  if (!url) return ''

  const regExp = /^.*(youtu.be\/|v\/|u\/\w\/|embed\/|watch\?v=|&v=)([^#&?]*).*/
  const match = url.match(regExp)

  return match && match[2].length === 11 ? match[2] : ''
}

/**
 * Check if URL is a YouTube video
 * @param {string} url - Video URL
 * @returns {boolean} - True if YouTube URL
 */
const isYouTubeUrl = (url) => {
  if (!url) return false
  return url.includes('youtube.com') || url.includes('youtu.be')
}

/**
 * Check if URL is a direct video file
 * @param {string} url - Video URL
 * @returns {boolean} - True if direct video file
 */
const isDirectVideoUrl = (url) => {
  if (!url) return false
  const videoExtensions = ['.mp4', '.webm', '.ogg', '.mov', '.avi']
  return videoExtensions.some((ext) => url.toLowerCase().includes(ext))
}

/**
 * VideoPlayer component for displaying YouTube videos and direct video files
 * @param {Object} props - Component props
 * @param {string} props.url - Video URL (YouTube or direct video file)
 * @param {number} props.offsetSeconds - Number of seconds to offset the video start time
 * @returns {JSX.Element} VideoPlayer component
 */
const VideoPlayer = ({ url, offsetSeconds }) => {
  const videoRef = useRef(null)

  // Use useEffect to autoplay direct video files when URL changes
  useEffect(() => {
    if (videoRef.current && url && isDirectVideoUrl(url)) {
      videoRef.current.load()
      // Attempt to autoplay (may be blocked by browser policies)
      const playPromise = videoRef.current.play()
      if (playPromise !== undefined) {
        playPromise.catch((error) => {
          // Autoplay was prevented, which is normal browser behavior
          console.log('Autoplay prevented:', error)
        })
      }
    }
  }, [url])

  // Handle offset for direct video files
  useEffect(() => {
    if (videoRef.current && offsetSeconds && isDirectVideoUrl(url)) {
      const handleLoadedData = () => {
        if (videoRef.current) {
          console.log('Setting video offset to:', offsetSeconds)
          videoRef.current.currentTime = offsetSeconds
        }
      }

      videoRef.current.addEventListener('loadeddata', handleLoadedData)

      // If video is already loaded, apply offset immediately
      if (videoRef.current.readyState >= 2) {
        handleLoadedData()
      }

      return () => {
        if (videoRef.current) {
          videoRef.current.removeEventListener('loadeddata', handleLoadedData)
        }
      }
    }
  }, [url, offsetSeconds])

  // Handle YouTube videos
  if (isYouTubeUrl(url)) {
    const videoId = extractYouTubeId(url)

    if (!videoId) {
      return (
        <div css={videoPlayerStyle}>
          <div
            css={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              height: '100%',
              color: '#9FA3A9',
              fontSize: '16px',
            }}
          >
            Invalid YouTube URL
          </div>
        </div>
      )
    }

    const startTime = offsetSeconds ? `&start=${Math.floor(offsetSeconds)}` : ''

    return (
      <div css={videoPlayerStyle}>
        <iframe
          key={`${url}-${offsetSeconds || 0}`}
          src={`https://www.youtube.com/embed/${videoId}?autoplay=1&mute=1&controls=1&modestbranding=1&rel=0${startTime}`}
          title='Video Player'
          allowFullScreen
          allow='accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture'
        />
      </div>
    )
  }

  // Handle direct video files (S3, etc.)
  if (isDirectVideoUrl(url)) {
    return (
      <div css={videoPlayerStyle}>
        <video
          ref={videoRef}
          controls
          autoPlay
          muted
          width='100%'
          height='100%'
          css={{
            objectFit: 'contain',
            backgroundColor: '#000',
          }}
        >
          <source src={url} type='video/mp4' />
          Your browser does not support the video tag.
        </video>
      </div>
    )
  }

  // No valid video URL provided
  return (
    <div css={videoPlayerStyle}>
      <div
        css={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          height: '100%',
          color: '#9FA3A9',
          fontSize: '16px',
        }}
      >
        No video available
      </div>
    </div>
  )
}

VideoPlayer.propTypes = {
  url: PropTypes.string,
  offsetSeconds: PropTypes.number,
}

VideoPlayer.defaultProps = {
  url: '',
  offsetSeconds: 0,
}

export default VideoPlayer
